import { FC, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';

import { GiftBoxNotice, ValidPeriodNotice } from './CouponNotices';
import ExchangeCautionNotice from './ExchangeCautionNotice';

interface CouponNoticeModalProps {
  open: boolean;
  validPeriod?: string;
  onClose: () => void;
}

/**
 * 교환신청 완료 · 발송완료 화면 진입 시 한 번 띄우는 안내 모달.
 * 1) 유효기간  2) 선물함 아님  3) 사용 유의사항 을 다음 → 다음 → 확인 순으로 안내한다.
 */
const CouponNoticeModal: FC<CouponNoticeModalProps> = ({
  open,
  validPeriod,
  onClose,
}) => {
  const [step, setStep] = useState(0);

  if (!open) return null;

  const steps = [
    {
      key: 'period',
      label: '유효기간',
      body: <ValidPeriodNotice validPeriod={validPeriod} />,
    },
    { key: 'giftbox', label: '수령 방법', body: <GiftBoxNotice /> },
    { key: 'caution', label: '사용 유의사항', body: <ExchangeCautionNotice /> },
  ];
  const isLast = step === steps.length - 1;

  const handleNext = () => {
    if (isLast) onClose();
    else setStep((s) => s + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-stone-800">받기 전 꼭 확인해 주세요</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-stone-400 transition hover:bg-stone-100"
            aria-label="닫기"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 단계 진행 표시 */}
        <div className="mt-3 flex items-center gap-2">
          <div className="flex flex-1 gap-1.5">
            {steps.map((s, i) => (
              <div
                key={s.key}
                className={`h-1.5 flex-1 rounded-full transition ${
                  i <= step ? 'bg-stone-700' : 'bg-stone-200'
                }`}
              />
            ))}
          </div>
          <span className="flex-shrink-0 text-xs font-medium text-stone-400">
            {step + 1} / {steps.length}
          </span>
        </div>

        {/* 단계별 안내 내용 */}
        <div className="mt-4">{steps[step]?.body}</div>

        {/* 이동 버튼 */}
        <div className="mt-5 flex gap-3">
          {step > 0 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex-1 rounded-lg border-2 border-stone-200 bg-white py-3 text-sm font-bold text-stone-600 transition active:bg-stone-100"
            >
              이전
            </button>
          )}
          <button
            onClick={handleNext}
            className="flex-1 rounded-lg bg-stone-800 py-3 text-sm font-bold text-white transition active:bg-stone-900"
          >
            {isLast ? '확인했습니다' : '다음'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponNoticeModal;
